import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faUsers } from "@fortawesome/free-solid-svg-icons";

const Field = ({ label, value }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    <div className="text-sm text-gray-800 bg-gray-50 px-3 py-2 rounded-md border border-gray-100">
      {value || "N/A"}
    </div>
  </div>
);

const ViewStaffAccount = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [staffDetails, setStaffDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getStaffDetails = async () => {
      setLoading(true);
      try {
        const response = await func.get(urls.GET_STAFF_DETAILS_BY_ID + id);
        if (response.status === 200) {
          const data = response.data.data;
          setStaffDetails({
            name: data.name || "N/A",
            emailId: data.emailId || "N/A",
            staffType: data.staffType || "N/A",
            status: data.status || "N/A",
            createdAt: data.createdAt
              ? new Date(data.createdAt).toLocaleDateString()
              : "N/A",
            updatedAt: data.updatedAt
              ? new Date(data.updatedAt).toLocaleDateString()
              : "N/A",
            staffBelongsTo: data.staffBelongsTo || "N/A",
            accessModules:
              data.accessModules?.length > 0
                ? data.accessModules.join(", ")
                : "N/A",
          });
        } else {
          toast.error("Failed to fetch staff details.");
          navigate("/staff-access-management/list");
        }
      } catch (error) {
        toast.error("Error fetching staff details.");
        navigate("/staff-access-management/list");
      } finally {
        setLoading(false);
      }
    };

    if (id) getStaffDetails();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6">
        <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm p-6 animate-pulse space-y-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-10 bg-gray-100 rounded-md" />
          ))}
        </div>
      </div>
    );
  }

  if (!staffDetails) {
    return (
      <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6 flex items-center justify-center">
        <p className="text-white">No Staff Data Found.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg transition text-white"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="text-sm" />
          </button>
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faUsers} className="text-white text-lg" />
            <h1 className="text-lg font-semibold text-white">Staff Details</h1>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
          {/* Personal Info */}
          <div>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Personal Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Name" value={staffDetails.name} />
              <Field label="Email" value={staffDetails.emailId} />
              <Field label="Staff Type" value={staffDetails.staffType} />
            </div>
          </div>

          <div>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Account Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Status" value={staffDetails.status} />
              <Field label="Created Date" value={staffDetails.createdAt} />
              <Field label="Updated Date" value={staffDetails.updatedAt} />
            </div>
          </div>

          {/* Conditional access info */}
          {staffDetails.staffType === "Staff" && (
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Access Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Staff Belongs To" value={staffDetails.staffBelongsTo} />
                <Field label="Access Modules" value={staffDetails.accessModules} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewStaffAccount;
